import { useState } from "react";
import type { KeyboardEvent, ReactElement, ReactNode } from "react";
import type { TicketBoard, TicketKarte, TicketSpalte, TicketZeile } from "@kundeninfo/api-client";
import { istWochenendTag, useSofortPopup } from "@kundeninfo/ui";
import { TicketZeitleiste, popupVon, wandereFokus } from "./TicketZeitleiste.js";
import type { AblageZiel } from "./TicketZeitleiste.js";
import { KOPF_ZONE_NAME, zeileFuer, zeilenText, zielVon, freieTage, feiertageImBereich, feiertageText } from "./ticketFormat.js";

/**
 * RASTERANSICHT (Spec „Rasteransicht“, 118): die Zeitleiste als gemeinsames Gitter über alle
 * Spalten mit Zeitleiste — Zeilen = Zeilen der Zeitleiste, Spalten = Spalten des Boards;
 * Spaltenkopf und Datumsspalte kleben (CSS). Spalten ohne Zeitleiste stehen daneben als Liste.
 * Eigenes Gitter als Übergang T-1 (docs/ME-DESIGN.md), bis me-design ein Zeitleisten-Gitter hat.
 *
 * Abgelegt wird wie im Board: Jede Zelle trägt das Ziel ihrer Spalte und Zeile (`zielVon`), der
 * Spaltenkopf das Ziel „Kopf“ (Datum bleibt), eine Spalte ohne Zeitleiste ebenso. Gezogen wird über die
 * Ziehhilfen aus me-design (`ziehbar` mit `spalten: '[data-tag]'`, F126): Jedes Ziel trägt `data-tag`,
 * `id` und `data-name` („In Arbeit · Di 20.10.“) für die Ansage. Tastaturweg: Leertaste am Griff und
 * „Verschieben …“ (F22); die Popups der freien Tage über die Datumsspalte (wandernder Tabstopp).
 */

type RasterProps = {
  board: TicketBoard; karte: (t: TicketKarte) => ReactNode; zielAttribute: (z: AblageZiel) => Record<string, string>;
};

function RasterZeile(p: { z: TicketZeile; mit: readonly TicketSpalte[]; zellen: Map<string, TicketKarte[]>;
                          props: RasterProps; feiertage: ReadonlyMap<string, unknown>; popup: string; tabstopp: boolean; onFokus: () => void;
                          vorlage: { gridTemplateColumns: string } }): ReactElement {
  const { z, props } = p;
  const ref = useSofortPopup<HTMLSpanElement>(p.popup);
  const wochenende = z.art === "TAG" && istWochenendTag(z.datum!);
  const feiertag = z.art === "TAG" && p.feiertage.has(z.datum!);
  // Wie die Zeitleiste: Eine Monatszeile nennt die Zahl ihrer Feiertage (die Namen im Popup).
  const imMonat = z.art === "MONAT" ? feiertageImBereich(z.von!, z.bis!, props.board.kalender) : [];
  const klassen = ["tr__zeile", `tr__zeile--${z.art.toLowerCase()}`, wochenende ? "tr__zeile--wochenende" : "",
    feiertag ? "tr__zeile--feiertag" : ""].filter((k) => k !== "").join(" ");
  return (
    <div className={klassen} role="row" style={p.vorlage} data-raster-zeile={z.index}>
      <span ref={ref} className="tr__datum" role="rowheader"
        {...(p.popup !== "" ? { tabIndex: p.tabstopp ? 0 : -1, onFocus: p.onFokus } : {})}>
        {zeilenText(z)}
        {imMonat.length > 0 && (
          <span className="tz__feiertage">{feiertageText(imMonat.length)}</span>
        )}
      </span>
      {p.mit.map((s) => (
        <div key={s.id} className="tr__zelle" role="cell" data-tag="" id={`tr-${s.id}-${z.index}`}
          data-name={zielVon(s, z).text} {...props.zielAttribute(zielVon(s, z))}>
          {(p.zellen.get(`${s.id}|${z.index}`) ?? []).map((t) => props.karte(t))}
        </div>
      ))}
    </div>
  );
}

export function TicketRaster(p: RasterProps): ReactElement {
  const [aktiv, setAktiv] = useState<number | null>(null);
  const mit = p.board.spalten.filter((s) => s.timelineMode !== "NONE");
  const ohne = p.board.spalten.filter((s) => s.timelineMode === "NONE");
  const feiertage = freieTage(p.board.kalender);
  const zellen = new Map<string, TicketKarte[]>();
  for (const t of p.board.tickets) {
    const schluessel = `${t.columnId}|${zeileFuer(t.resubmitDate, p.board.zeilen, p.board.heute)}`;
    zellen.set(schluessel, [...(zellen.get(schluessel) ?? []), t]);
  }
  // Feste Spaltenbreite wie im Board (Entwurf v001): Die Karte bricht ihren Titel um, statt die Spalte
  // auf die Länge des längsten Titels zu dehnen.
  const vorlage = { gridTemplateColumns: `8em repeat(${mit.length}, minmax(240px, 320px))` };
  const popups = new Map(p.board.zeilen.map((z) => [z.index, popupVon(z, p.board.kalender)]));
  const mitPopup = p.board.zeilen.filter((z) => popups.get(z.index) !== "").map((z) => z.index);
  const stopp = aktiv !== null && mitPopup.includes(aktiv) ? aktiv : mitPopup[0];
  const tasten = (e: KeyboardEvent<HTMLDivElement>) => {
    const naechster = wandereFokus(e, ".tr__datum[tabindex]");
    if (naechster !== null) {
      setAktiv(Number(naechster.closest("[data-raster-zeile]")?.getAttribute("data-raster-zeile")));
    }
  };
  return (
    <div className="tr__wrapper">
      <div className="tr" role="table" aria-label="Raster der Zeitleiste" onKeyDown={tasten}>
        <div className="tr__zeile tr__kopf" role="row" style={vorlage}>
          <span className="tr__ecke" role="columnheader">Zeitleiste</span>
          {mit.map((s) => (
            <span key={s.id} className="tr__spaltenkopf" role="columnheader" data-tag="" id={`tr-${s.id}-kopf`}
              data-name={`${s.name} · ${KOPF_ZONE_NAME}`}
              {...p.zielAttribute({ columnId: s.id, ziel: "KOPF", datum: null, text: `${s.name} · ${KOPF_ZONE_NAME}` })}>{s.name}</span>
          ))}
        </div>
        {p.board.zeilen.map((z) => (
          <RasterZeile key={z.index} z={z} mit={mit} zellen={zellen} props={p} vorlage={vorlage} feiertage={feiertage}
            popup={popups.get(z.index) ?? ""} tabstopp={z.index === stopp} onFokus={() => setAktiv(z.index)} />
        ))}
      </div>
      {ohne.map((s) => (
        <section key={s.id} className="tr__ohne" role="region" aria-label={s.name} data-tag="" id={`tr-${s.id}`}
          data-name={s.name} {...p.zielAttribute({ columnId: s.id, ziel: "KOPF", datum: null, text: s.name })}>
          <header className="tr__ohnekopf"><h3 className="tr__ohnename">{s.name}</h3></header>
          <TicketZeitleiste spalte={s} zeilen={p.board.zeilen} heute={p.board.heute}
            tickets={p.board.tickets.filter((t) => t.columnId === s.id)} kalender={p.board.kalender}
            offen={false} karte={p.karte} zielAttribute={p.zielAttribute} />
        </section>
      ))}
    </div>
  );
}
